import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { leaveAffiliationMembership, updateAffiliation } from '../../services/authService';
import { fetchClubs } from '../../services/clubService';
import { fetchCommunities } from '../../services/communityService';
import { fetchOrganizations } from '../../services/organizationService';
import { fetchTeamAdminsForCommunity } from '../../services/communityTeamAdminService';
import { fetchTeamAdminsForClub, fetchTeamAdminsForOrganization } from '../../services/teamAdminService';
import Section from '../common/Section';

const normalizeMembershipStatus = (value) => String(value || '').trim().toLowerCase();

const resolveMembershipEntityId = (membership) => {
  if (!membership) return '';

  return String(
    membership.entityId ||
    membership.entity?._id ||
    membership.entity?.id ||
    membership._id ||
    ''
  ).trim();
};

const buildMembershipMapByEntityId = (memberships = []) =>
  memberships.reduce((map, membership) => {
    const entityId = resolveMembershipEntityId(membership);
    if (!entityId) return map;

    map.set(entityId, membership);
    return map;
  }, new Map());

const resolveMembershipDisplayStatus = (membership) => {
  if (!membership) return '';

  return normalizeMembershipStatus(
    membership.approvalStatus ||
      membership?.approval?.status ||
      membership.membershipStatus ||
      membership.status ||
      membership.state
  );
};

const isApprovedActiveMembership = (membership) => {
  if (!membership) return false;

  const approvalStatus = normalizeMembershipStatus(
    membership.approvalStatus || membership?.approval?.status
  );

  const membershipStatus = normalizeMembershipStatus(
    membership.membershipStatus || membership.status
  );

  if (approvalStatus && approvalStatus !== 'approved') {
    return false;
  }

  if (membershipStatus) {
    return membershipStatus === 'active' || membershipStatus === 'approved';
  }

  if (membership.isActive === true) {
    return true;
  }

  return approvalStatus === 'approved';
};


const normalizeId = (value) => String(value || '').trim();

const TAB_KEYS = {
  ORGANIZATION: 'organization',
  CLUB: 'club',
  COMMUNITY: 'community',
};

const AccountAffiliationSection = ({ embedded = false }) => {
  const { token, refreshUser, updateUser, user } = useAuth();

  const [activeTab, setActiveTab] = useState(TAB_KEYS.ORGANIZATION);

  const [form, setForm] = useState({
    organizationId: '',
    clubId: '',
    communityId: '',
    organizationIds: [],
    clubIds: [],
    communityIds: [],
    organizationMemberships: [],
    clubMemberships: [],
    communityMemberships: [],
    primaryOrganizationId: '',
    primaryClubId: '',
    primaryCommunityId: '',
    teamAdminId: '',
    clubAdminId: '',
    selectedOrganizationAdminUserId: '',
    selectedClubAdminUserId: '',
    requestedRole: 'department_admin',
    selectedDepartmentAdminUserId: '',
    selectedCommunityAdminUserId: '',
  });

  const [pendingOrganizationId, setPendingOrganizationId] = useState('');
  const [pendingClubId, setPendingClubId] = useState('');
  const [pendingCommunityId, setPendingCommunityId] = useState('');

  const [organizations, setOrganizations] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [communities, setCommunities] = useState([]);

  const [organizationAdmins, setOrganizationAdmins] = useState([]);
  const [clubAdmins, setClubAdmins] = useState([]);
  const [communityAdmins, setCommunityAdmins] = useState([]);

  const [status, setStatus] = useState({ message: '', tone: 'info' });

  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(true);
  const [isLoadingClubs, setIsLoadingClubs] = useState(true);
  const [isLoadingCommunities, setIsLoadingCommunities] = useState(true);

  const [isLoadingOrganizationAdmins, setIsLoadingOrganizationAdmins] = useState(false);
  const [isLoadingClubAdmins, setIsLoadingClubAdmins] = useState(false);
  const [isLoadingCommunityAdmins, setIsLoadingCommunityAdmins] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const approvalMessage =
    user?.approvalStatus === 'pending'
      ? 'Your request is yet to be approved by the department admin.'
      : '';

  const organizationLabelMap = useMemo(
    () => new Map(organizations.map((organization) => [organization._id, organization.name])),
    [organizations]
  );

  const clubLabelMap = useMemo(
    () => new Map(clubs.map((club) => [club._id || club.id, club.name])),
    [clubs]
  );

  const communityLabelMap = useMemo(
    () => new Map(communities.map((community) => [community._id, community.name])),
    [communities]
  );

  const availableOrganizations = useMemo(
    () => organizations.filter((organization) => !form.organizationIds.includes(organization._id)),
    [organizations, form.organizationIds]
  );

  const availableClubs = useMemo(
    () =>
      clubs.filter((club) => {
        const clubId = club._id || club.id;
        return !form.clubIds.includes(clubId);
      }),
    [clubs, form.clubIds]
  );

  const availableCommunities = useMemo(
    () => communities.filter((community) => !form.communityIds.includes(community._id)),
    [communities, form.communityIds]
  );

  const primaryEligibilityByType = useMemo(() => {
    const buildEligibilityMap = (memberships = []) =>
      memberships.reduce((map, membership) => {
        const entityId = resolveMembershipEntityId(membership);
        if (!entityId) return map;

        map.set(entityId, isApprovedActiveMembership(membership));
        return map;
      }, new Map());

    return {
      organization: buildEligibilityMap(user?.organizations),
      club: buildEligibilityMap(user?.clubs),
      community: buildEligibilityMap(user?.communities),
    };
  }, [user]);

  const membershipByType = useMemo(
    () => ({
      organization: buildMembershipMapByEntityId(user?.organizations),
      club: buildMembershipMapByEntityId(user?.clubs),
      community: buildMembershipMapByEntityId(user?.communities),
    }),
    [user]
  );

  const canSetPrimaryForType = (type, id) => {
    const key = String(id || '').trim();
    return Boolean(primaryEligibilityByType[type]?.get(key));
  };

  useEffect(() => {
    if (!token) return;

    refreshUser().catch(() => {
      // no-op: keep existing user snapshot if refresh fails
    });
  }, [token, refreshUser]);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const organizationResponse = await fetchOrganizations();
        setOrganizations(organizationResponse.organizations || []);
      } catch (error) {
        setStatus({
          message: error.message || 'Unable to load organization choices.',
          tone: 'danger',
        });
      } finally {
        setIsLoadingOrganizations(false);
      }

      try {
        const clubResponse = await fetchClubs();
        setClubs(clubResponse.clubs || []);
      } catch (error) {
        setStatus((prev) => ({
          message: prev.message || error.message || 'Unable to load club choices.',
          tone: prev.message ? prev.tone : 'danger',
        }));
      } finally {
        setIsLoadingClubs(false);
      }

      try {
        const communityResponse = await fetchCommunities();
        setCommunities(communityResponse.communities || []);
      } catch (error) {
        setStatus((prev) => ({
          message: prev.message || error.message || 'Unable to load community choices.',
          tone: prev.message ? prev.tone : 'danger',
        }));
      } finally {
        setIsLoadingCommunities(false);
      }
    };

    loadLookups();
  }, []);

  useEffect(() => {
    const userOrganizationIds = Array.from(
      new Set((user?.organizations || []).map(resolveMembershipEntityId).filter(Boolean))
    );

    const userClubIds = Array.from(
      new Set((user?.clubs || []).map(resolveMembershipEntityId).filter(Boolean))
    );

    const userCommunityIds = Array.from(
      new Set((user?.communities || []).map(resolveMembershipEntityId).filter(Boolean))
    );
    const userCommunityMemberships = (user?.communities || [])
      .map((membership) => {
        const entityId = resolveMembershipEntityId(membership);
        if (!entityId) return null;

        const approverId = membership.approverId || membership.approver?.id || membership.approver?._id || '';
        const status = normalizeMembershipStatus(membership.status) || 'pending';

        return {
          entityId,
          approverId,
          status,
        };
      })
      .filter(Boolean);

    const userOrganizationMemberships = (user?.organizations || [])
      .map((membership) => {
        const entityId = resolveMembershipEntityId(membership);
        if (!entityId) return null;

        const approverId = membership.approverId || membership.approver?.id || membership.approver?._id || '';
        const status = normalizeMembershipStatus(membership.status) || 'pending';

        return {
          entityId,
          approverId,
          status,
        };
      })
      .filter(Boolean);

    const userClubMemberships = (user?.clubs || [])
      .map((membership) => {
        const entityId = resolveMembershipEntityId(membership);
        if (!entityId) return null;

        const approverId = membership.approverId || membership.approver?.id || membership.approver?._id || '';
        const status = normalizeMembershipStatus(membership.status) || 'pending';

        return {
          entityId,
          approverId,
          status,
        };
      })
      .filter(Boolean);

    const userOrganizationId = user?.primaryOrganizationId || user?.organization?.id || '';
    const userClubId = user?.primaryClubId || user?.club?.id || '';
    const userCommunityId = user?.primaryCommunityId || user?.community?.id || '';

    const userTeamAdminId = user?.teamAdmins?.[0]?.id || '';

    setForm({
      organizationId: userOrganizationId,
      clubId: userClubId,
      communityId: userCommunityId,
      organizationIds: userOrganizationIds,
      clubIds: userClubIds,
      communityIds: userCommunityIds,
      organizationMemberships: userOrganizationMemberships,
      clubMemberships: userClubMemberships,
      communityMemberships: userCommunityMemberships,
      primaryOrganizationId: userOrganizationId,
      primaryClubId: userClubId,
      primaryCommunityId: userCommunityId,
      teamAdminId: userOrganizationId ? userTeamAdminId : '',
      clubAdminId: userClubId ? userTeamAdminId : '',
      selectedOrganizationAdminUserId: '',
      selectedClubAdminUserId: '',
      requestedRole: 'department_admin',
      selectedDepartmentAdminUserId: userCommunityId ? userTeamAdminId : '',
      selectedCommunityAdminUserId: '',
    });
  }, [user]);

  useEffect(() => {
    const loadOrganizationAdmins = async () => {
      const targetOrganizationId = pendingOrganizationId || '';

      if (!targetOrganizationId) {
        setOrganizationAdmins([]);
        setIsLoadingOrganizationAdmins(false);
        return;
      }

      setIsLoadingOrganizationAdmins(true);
      setStatus({ message: '', tone: 'info' });

      try {
        const response = await fetchTeamAdminsForOrganization(targetOrganizationId);
        setOrganizationAdmins(response.teamAdmins || []);
      } catch (error) {
        setStatus({
          message: error.message || 'Unable to load organization admins.',
          tone: 'danger',
        });
      } finally {
        setIsLoadingOrganizationAdmins(false);
      }
    };

    loadOrganizationAdmins();
  }, [pendingOrganizationId]);

  useEffect(() => {
    const loadClubAdmins = async () => {
      const targetClubId = pendingClubId || '';

      if (!targetClubId) {
        setClubAdmins([]);
        setIsLoadingClubAdmins(false);
        return;
      }

      setIsLoadingClubAdmins(true);
      setStatus({ message: '', tone: 'info' });

      try {
        const response = await fetchTeamAdminsForClub(targetClubId);
        setClubAdmins(response.teamAdmins || []);
      } catch (error) {
        setStatus({
          message: error.message || 'Unable to load club admins.',
          tone: 'danger',
        });
      } finally {
        setIsLoadingClubAdmins(false);
      }
    };

    loadClubAdmins();
  }, [pendingClubId]);

  useEffect(() => {
    const resolveCommunitySelectionId = (communityRef) => {
      if (typeof communityRef === 'string') {
        return communityRef.trim();
      }

      if (!communityRef || typeof communityRef !== 'object') {
        return '';
      }

      return String(
        communityRef.entityId ||
        communityRef.id ||
        communityRef._id ||
        ''
      ).trim();
    };

    const loadCommunityAdmins = async () => {
      const targetCommunityId =
        pendingCommunityId ||
        resolveCommunitySelectionId(form.primaryCommunityId) ||
        resolveCommunitySelectionId(form.communityIds[0]) ||
        '';

      if (!targetCommunityId) {
        setCommunityAdmins([]);
        setIsLoadingCommunityAdmins(false);
        return;
      }

      setIsLoadingCommunityAdmins(true);
      setStatus({ message: '', tone: 'info' });

      try {
        const response = await fetchTeamAdminsForCommunity(targetCommunityId);
        setCommunityAdmins(response.teamAdmins || []);
      } catch (error) {
        setStatus({
          message: error.message || 'Unable to load community admins.',
          tone: 'danger',
        });
      } finally {
        setIsLoadingCommunityAdmins(false);
      }
    };

    loadCommunityAdmins();
  }, [pendingCommunityId, form.primaryCommunityId, form.communityIds]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setStatus({ message: '', tone: 'info' });

    if (name === 'requestedRole') {
      setForm((prev) => ({
        ...prev,
        requestedRole: value,
        selectedDepartmentAdminUserId:
          value === 'department_admin' ? prev.selectedDepartmentAdminUserId : '',
        selectedCommunityAdminUserId:
          value === 'community_admin' ? prev.selectedCommunityAdminUserId : '',
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddMembership = (type, id) => {
    if (!id) return;

    const pluralKey = `${type}Ids`;
    const primaryKey = `primary${type.charAt(0).toUpperCase()}${type.slice(1)}Id`;
    const legacyKey = `${type}Id`;

    setForm((prev) => {
      if (prev[pluralKey].includes(id)) {
        return prev;
      }

      const nextIds = [...prev[pluralKey], id];
      const nextState = {
        ...prev,
        [pluralKey]: nextIds,
        [primaryKey]: prev[primaryKey] || '',
        [legacyKey]: prev[primaryKey] || '',
      };

      if (type === 'community') {
        const existingMemberships = Array.isArray(prev.communityMemberships) ? prev.communityMemberships : [];
        const nextCommunityMemberships = existingMemberships.filter((membership) => membership.entityId !== id);
        nextCommunityMemberships.push({
          entityId: id,
          approverId: prev.selectedDepartmentAdminUserId || '',
          status: 'pending',
        });
        nextState.communityMemberships = nextCommunityMemberships;
      }

      if (type === 'organization') {
        const existingMemberships = Array.isArray(prev.organizationMemberships) ? prev.organizationMemberships : [];
        const nextOrganizationMemberships = existingMemberships.filter((membership) => membership.entityId !== id);
        nextOrganizationMemberships.push({
          entityId: id,
          approverId: prev.selectedOrganizationAdminUserId || '',
          status: 'pending',
        });
        nextState.organizationMemberships = nextOrganizationMemberships;
      }

      if (type === 'club') {
        const existingMemberships = Array.isArray(prev.clubMemberships) ? prev.clubMemberships : [];
        const nextClubMemberships = existingMemberships.filter((membership) => membership.entityId !== id);
        nextClubMemberships.push({
          entityId: id,
          approverId: prev.selectedClubAdminUserId || '',
          status: 'pending',
        });
        nextState.clubMemberships = nextClubMemberships;
      }

      return nextState;
    });
  };

  const handleRemoveMembership = (type, id) => {
    const pluralKey = `${type}Ids`;
    const primaryKey = `primary${type.charAt(0).toUpperCase()}${type.slice(1)}Id`;
    const legacyKey = `${type}Id`;

    setForm((prev) => {
      const nextIds = prev[pluralKey].filter((itemId) => itemId !== id);
      const nextPrimary =
        prev[primaryKey] === id
          ? nextIds.find((itemId) => canSetPrimaryForType(type, String(itemId))) || ''
          : prev[primaryKey];

      return {
        ...prev,
        [pluralKey]: nextIds,
        organizationMemberships:
          type === 'organization'
            ? (prev.organizationMemberships || []).filter((membership) => membership.entityId !== id)
            : prev.organizationMemberships,
        clubMemberships:
          type === 'club'
            ? (prev.clubMemberships || []).filter((membership) => membership.entityId !== id)
            : prev.clubMemberships,
        communityMemberships:
          type === 'community'
            ? (prev.communityMemberships || []).filter((membership) => membership.entityId !== id)
            : prev.communityMemberships,
        [primaryKey]: nextPrimary,
        [legacyKey]: nextPrimary,
        teamAdminId: type === 'organization' && nextPrimary === '' ? '' : prev.teamAdminId,
        clubAdminId: type === 'club' && nextPrimary === '' ? '' : prev.clubAdminId,
        selectedDepartmentAdminUserId:
          type === 'community' && nextPrimary === ''
            ? ''
            : prev.selectedDepartmentAdminUserId,
        selectedCommunityAdminUserId:
          type === 'community' && nextPrimary === ''
            ? ''
            : prev.selectedCommunityAdminUserId,
      };
    });
  };

  const handlePrimaryMembershipChange = (type, id) => {
    if (!canSetPrimaryForType(type, id)) {
      return;
    }

    const primaryKey = `primary${type.charAt(0).toUpperCase()}${type.slice(1)}Id`;
    const legacyKey = `${type}Id`;

    setForm((prev) => ({
      ...prev,
      [primaryKey]: id,
      [legacyKey]: id,
      teamAdminId: type === 'organization' ? '' : prev.teamAdminId,
      clubAdminId: type === 'club' ? '' : prev.clubAdminId,
      selectedDepartmentAdminUserId:
        type === 'community' ? '' : prev.selectedDepartmentAdminUserId,
      selectedCommunityAdminUserId:
        type === 'community' ? '' : prev.selectedCommunityAdminUserId,
    }));
  };


  const handlePendingCommunityChange = (event) => {
    const nextCommunityId = event.target.value;
    setStatus({ message: '', tone: 'info' });

    setPendingCommunityId(nextCommunityId);
    setForm((prev) => ({
      ...prev,
      selectedDepartmentAdminUserId: '',
      communityMemberships: (prev.communityMemberships || []).map((membership) => ({
        ...membership,
        approverId: '',
      })),
    }));
  };

  const handleLeaveMembership = async (entityType, entityId) => {
    if (!token) return;

    try {
      const payload = await leaveAffiliationMembership({ entityType, entityId }, token);
      if (payload.user) {
        updateUser(payload.user);
      }
      setStatus({ message: payload.message || 'Membership removed.', tone: 'success' });
    } catch (error) {
      setStatus({ message: error.message || 'Unable to remove membership.', tone: 'danger' });
    }
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    if (!token) {
      setStatus({
        message: 'Please log in to update your organization, club, or community.',
        tone: 'danger',
      });
      return;
    }

    setIsSubmitting(true);
    setStatus({ message: '', tone: 'info' });

    try {
      const requiresCommunityApprover = form.requestedRole === 'department_admin';

      const resolvePrimaryId = (type) => {
        const primaryKey = `primary${type.charAt(0).toUpperCase()}${type.slice(1)}Id`;
        const pluralKey = `${type}Ids`;
        const primaryId = form[primaryKey];
        const isIncluded = form[pluralKey].includes(primaryId);

        if (!primaryId || !isIncluded || !canSetPrimaryForType(type, primaryId)) {
          return '';
        }

        return primaryId;
      };

      const primaryOrganizationId = resolvePrimaryId('organization');
      const primaryClubId = resolvePrimaryId('club');
      const primaryCommunityId = resolvePrimaryId('community');
      const selectedCommunityAdminId = normalizeId(form.selectedDepartmentAdminUserId);
      const selectedAdminBelongsToCommunity =
        !selectedCommunityAdminId ||
        communityAdmins.some((admin) => normalizeId(admin._id || admin.id) === selectedCommunityAdminId);

      if (!selectedAdminBelongsToCommunity) {
        setStatus({
          message: 'Please select a department admin from the selected community.',
          tone: 'danger',
        });
        return;
      }
      const selectedOrganizationIds = Array.isArray(form.organizationIds) ? form.organizationIds : [];
      const selectedClubIds = Array.isArray(form.clubIds) ? form.clubIds : [];
      const selectedCommunityIds = Array.isArray(form.communityIds) ? form.communityIds : [];
      const organizationMembershipsByEntityId = new Map();
      const clubMembershipsByEntityId = new Map();
      const communityMembershipsByEntityId = new Map();

      (form.organizationMemberships || []).forEach((membership) => {
        const entityId = String(membership?.entityId || '').trim();
        if (!entityId || !selectedOrganizationIds.includes(entityId)) return;

        const approverId = String(membership?.approverId || '').trim();
        if (!approverId) return;

        if (!organizationMembershipsByEntityId.has(entityId)) {
          organizationMembershipsByEntityId.set(entityId, {
            entityId,
            approverId,
          });
        }
      });

      (form.clubMemberships || []).forEach((membership) => {
        const entityId = String(membership?.entityId || '').trim();
        if (!entityId || !selectedClubIds.includes(entityId)) return;

        const approverId = String(membership?.approverId || '').trim();
        if (!approverId) return;

        if (!clubMembershipsByEntityId.has(entityId)) {
          clubMembershipsByEntityId.set(entityId, {
            entityId,
            approverId,
          });
        }
      });

      (form.communityMemberships || []).forEach((membership) => {
        const entityId = String(membership?.entityId || '').trim();
        if (!entityId || !selectedCommunityIds.includes(entityId)) return;

        const approverId = String(membership?.approverId || '').trim();
        if (requiresCommunityApprover && !approverId) return;

        if (!communityMembershipsByEntityId.has(entityId)) {
          communityMembershipsByEntityId.set(entityId, {
            entityId,
            approverId,
          });
        }
      });

      const affiliationPayload = {
        organizationIds: form.organizationIds,
        clubIds: form.clubIds,
        communityIds: selectedCommunityIds,
        organizationMemberships: Array.from(organizationMembershipsByEntityId.values()),
        clubMemberships: Array.from(clubMembershipsByEntityId.values()),
        communityMemberships: Array.from(communityMembershipsByEntityId.values()),
      };

      if (primaryOrganizationId) {
        affiliationPayload.primaryOrganizationId = primaryOrganizationId;
      }

      if (primaryClubId) {
        affiliationPayload.primaryClubId = primaryClubId;
      }

      if (primaryCommunityId) {
        affiliationPayload.primaryCommunityId = primaryCommunityId;
      }

      if (form.communityIds.length > 0) {
        affiliationPayload.requestedRole = form.requestedRole;
      }

      if (form.requestedRole === 'community_admin' && form.selectedCommunityAdminUserId) {
        affiliationPayload.communityAdminUserId = form.selectedCommunityAdminUserId;
      }

      const payload = await updateAffiliation(affiliationPayload, token);
      if (payload.user) {
        updateUser(payload.user);
      }

      await refreshUser();

      setStatus({
        message: payload.message || 'Affiliation updated successfully.',
        tone: 'success',
      });
    } catch (error) {
      setStatus({ message: error.message || 'Unable to update affiliation.', tone: 'danger' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderMembershipChips = (type, ids, labelMap, membershipMap) => {
    if (!ids.length) {
      return <p className="text-muted small mb-0">No selections yet.</p>;
    }

    const primaryKey = `primary${type.charAt(0).toUpperCase()}${type.slice(1)}Id`;
    return (
      <div className="d-flex flex-column gap-2 mt-3">
        {ids.map((id) => {
            const canSetPrimary = canSetPrimaryForType(type, id);
            const membership = membershipMap?.get(id);
            const membershipStatus = resolveMembershipDisplayStatus(membership);
            const isPending = membershipStatus === 'pending';

            return (
              <div
                key={`${type}-${id}`}
                className="badge bg-light text-dark border d-flex align-items-center gap-2 p-2 text-wrap"
              >
                <input
                  type="radio"
                  className="form-check-input mt-0"
                  disabled={!canSetPrimary}
                  checked={form[primaryKey] === id}
                  onChange={() => handlePrimaryMembershipChange(type, id)}
                  aria-label={`Set primary ${type}`}
                />
                <span>
                  {labelMap.get(id) || id}
                  {form[primaryKey] === id ? ' (Primary)' : ''}
                  {isPending ? ' (Pending approval)' : ''}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-link text-danger p-0"
                  onClick={() => handleRemoveMembership(type, id)}
                >
                  Remove
                </button>
              </div>
            );
          })}
      </div>
    );
  };

  const tabButtonClass = (tabKey) =>
    `nav-link ${activeTab === tabKey ? 'active fw-semibold' : 'text-body'}`;

  const content = (
    <>
      <Section
        eyebrow=""
        title="Affiliation"
        description="Manage your organization, club, and community memberships from separate tabs."
      >
        {approvalMessage && (
          <div className="alert alert-warning mb-3" role="status">
            {approvalMessage}
          </div>
        )}

        {status.message && (
          <div className={`alert alert-${status.tone} mb-3`} role="status">
            {status.message}
          </div>
        )}

        <div className="mb-4">
          <p className="small text-muted mb-2">Current memberships</p>
          <div className="d-flex flex-wrap gap-2">
            {(user?.organizations || []).map((item) => (
              <button
                key={`org-${item.id}`}
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => handleLeaveMembership('organization', item.entityId)}
              >
                Org: {item?.entity?.name || item.entityId} ×
              </button>
            ))}

            {(user?.communities || []).map((item) => (
              <button
                key={`community-${item.id}`}
                type="button"
                className="btn btn-sm btn-outline-success"
                onClick={() => handleLeaveMembership('community', item.entityId)}
              >
                Community: {item?.entity?.name || item.entityId} ×
              </button>
            ))}

            {(user?.clubs || []).map((item) => (
              <button
                key={`club-${item.id}`}
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => handleLeaveMembership('club', item.entityId)}
              >
                Club: {item?.entity?.name || item.entityId} ×
              </button>
            ))}

            {!user?.organizations?.length &&
              !user?.communities?.length &&
              !user?.clubs?.length && (
                <span className="text-muted small">No memberships added yet.</span>
              )}
          </div>
        </div>

        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button
              type="button"
              className={tabButtonClass(TAB_KEYS.ORGANIZATION)}
              onClick={() => setActiveTab(TAB_KEYS.ORGANIZATION)}
            >
              Organization
              {form.organizationIds.length > 0 && (
                <span className="badge bg-primary-subtle text-primary ms-2">
                  {form.organizationIds.length}
                </span>
              )}
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={tabButtonClass(TAB_KEYS.CLUB)}
              onClick={() => setActiveTab(TAB_KEYS.CLUB)}
            >
              Club
              {form.clubIds.length > 0 && (
                <span className="badge bg-secondary-subtle text-secondary ms-2">
                  {form.clubIds.length}
                </span>
              )}
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={tabButtonClass(TAB_KEYS.COMMUNITY)}
              onClick={() => setActiveTab(TAB_KEYS.COMMUNITY)}
            >
              Community
              {form.communityIds.length > 0 && (
                <span className="badge bg-success-subtle text-success ms-2">
                  {form.communityIds.length}
                </span>
              )}
            </button>
          </li>
        </ul>

        <div className="border rounded-3 p-3 p-md-4 bg-white">
          {activeTab === TAB_KEYS.ORGANIZATION && (
            <div className="affiliation-section">

              <div className="mb-3">
                <h6 className="fw-semibold mb-1">Join an Organization</h6>
                <p className="text-muted small mb-0">
                  Select an organization and the admin who will approve your request.
                </p>
              </div>

              <div className="row g-2 align-items-end">

                {/* Organization Select */}
                <div className="col-md-5">
                  <label className="form-label small fw-semibold">
                    Organization
                  </label>

                  <select
                    className="form-select"
                    value={pendingOrganizationId}
                    onChange={(event) => setPendingOrganizationId(event.target.value)}
                    disabled={isLoadingOrganizations || availableOrganizations.length === 0}
                  >
                    <option value="">
                      {isLoadingOrganizations ? "Loading organizations..." : "Select organization"}
                    </option>

                    {availableOrganizations.map((organization) => (
                      <option key={organization._id} value={organization._id}>
                        {organization.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Organization Admin Select */}
                <div className="col-md-5">
                  <label className="form-label small fw-semibold">
                    Organization Admin
                  </label>

                  <select
                    className="form-select"
                    name="selectedOrganizationAdminUserId"
                    value={form.selectedOrganizationAdminUserId}
                    onChange={handleChange}
                    disabled={isLoadingOrganizationAdmins || organizationAdmins.length === 0}
                  >
                    <option value="">
                      {isLoadingOrganizationAdmins ? "Loading admins..." : "Select approver"}
                    </option>

                    {organizationAdmins.map((organizationAdmin) => (
                      <option key={organizationAdmin.id} value={organizationAdmin.id}>
                        {organizationAdmin.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Add Button */}
                <div className="col-md-2 d-grid">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!pendingOrganizationId || !form.selectedOrganizationAdminUserId}
                    onClick={() => {
                      handleAddMembership("organization", pendingOrganizationId);
                      setPendingOrganizationId("");
                      setForm((prev) => ({
                        ...prev,
                        selectedOrganizationAdminUserId: "",
                      }));
                    }}
                  >
                    Add
                  </button>
                </div>

              </div>

              {/* Selected Organizations */}
              <div className="mt-4">
                {renderMembershipChips(
                  "organization",
                  form.organizationIds,
                  organizationLabelMap,
                  membershipByType.organization
                )}
              </div>

              {/* Empty States */}

              {!isLoadingOrganizations && organizations.length === 0 && (
                <div className="alert alert-warning mt-3 mb-0">
                  No organizations are available yet.
                </div>
              )}

              {!isLoadingOrganizations &&
                organizations.length > 0 &&
                availableOrganizations.length === 0 && (
                  <div className="text-muted small mt-3">
                    All organizations already selected.
                  </div>
                )}

            </div>
          )}

          {activeTab === TAB_KEYS.CLUB && (
            <div className="affiliation-section">

              <div className="mb-3">
                <h6 className="fw-semibold mb-1">Join a Club</h6>
                <p className="text-muted small mb-0">
                  Select a club and the club admin who will approve your request.
                </p>
              </div>

              <div className="row g-2 align-items-end">

                {/* Club Select */}
                <div className="col-md-5">
                  <label className="form-label small fw-semibold">
                    Club
                  </label>

                  <select
                    className="form-select"
                    value={pendingClubId}
                    onChange={(event) => setPendingClubId(event.target.value)}
                    disabled={isLoadingClubs || availableClubs.length === 0}
                  >
                    <option value="">
                      {isLoadingClubs ? "Loading clubs..." : "Select club"}
                    </option>

                    {availableClubs.map((club) => (
                      <option key={club._id || club.id} value={club._id || club.id}>
                        {club.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Club Admin Select */}
                <div className="col-md-5">
                  <label className="form-label small fw-semibold">
                    Club Admin
                  </label>

                  <select
                    className="form-select"
                    name="selectedClubAdminUserId"
                    value={form.selectedClubAdminUserId}
                    onChange={handleChange}
                    disabled={isLoadingClubAdmins || clubAdmins.length === 0}
                  >
                    <option value="">
                      {isLoadingClubAdmins ? "Loading admins..." : "Select approver"}
                    </option>

                    {clubAdmins.map((clubAdmin) => (
                      <option key={clubAdmin.id} value={clubAdmin.id}>
                        {clubAdmin.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Add Button */}
                <div className="col-md-2 d-grid">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!pendingClubId || !form.selectedClubAdminUserId}
                    onClick={() => {
                      handleAddMembership("club", pendingClubId);
                      setPendingClubId("");
                      setForm((prev) => ({
                        ...prev,
                        selectedClubAdminUserId: "",
                      }));
                    }}
                  >
                    Add
                  </button>
                </div>

              </div>

              {/* Selected Clubs */}
              <div className="mt-4">
                {renderMembershipChips(
                  "club",
                  form.clubIds,
                  clubLabelMap,
                  membershipByType.club
                )}
              </div>

              {/* Empty States */}

              {!isLoadingClubs && clubs.length === 0 && (
                <div className="alert alert-warning mt-3 mb-0">
                  No clubs are available yet.
                </div>
              )}

              {!isLoadingClubs && clubs.length > 0 && availableClubs.length === 0 && (
                <div className="text-muted small mt-3">
                  All clubs are already selected.
                </div>
              )}

            </div>
          )}

          {activeTab === TAB_KEYS.COMMUNITY && (
            <div className="affiliation-section">

              <div className="mb-3">
                <h6 className="fw-semibold mb-1">Join a Community</h6>
                <p className="text-muted small mb-0">
                  Select a community and the department admin who will approve your request.
                </p>
              </div>

              <div className="row g-2 align-items-end">

                {/* Community Select */}
                <div className="col-md-5">
                  <label className="form-label small fw-semibold">
                    Community
                  </label>

                  <select
                    className="form-select"
                    value={pendingCommunityId}
                    onChange={handlePendingCommunityChange}
                    disabled={isLoadingCommunities || availableCommunities.length === 0}
                  >
                    <option value="">
                      {isLoadingCommunities
                        ? "Loading communities..."
                        : "Select community"}
                    </option>

                    {availableCommunities.map((community) => (
                      <option key={community._id} value={community._id}>
                        {community.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Department Admin Select */}
                <div className="col-md-5">
                  <label className="form-label small fw-semibold">
                    Department Admin
                  </label>

                  <select
                    className="form-select"
                    name="selectedDepartmentAdminUserId"
                    value={form.selectedDepartmentAdminUserId}
                    onChange={handleChange}
                    disabled={isLoadingCommunityAdmins || communityAdmins.length === 0}
                  >
                    <option value="">
                      {isLoadingCommunityAdmins
                        ? "Loading admins..."
                        : "Select approver"}
                    </option>

                    {communityAdmins.map((admin) => (
                      <option key={admin.id} value={admin.id}>
                        {admin.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Add Button */}
                <div className="col-md-2 d-grid">
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!pendingCommunityId || !form.selectedDepartmentAdminUserId}
                    onClick={() => {
                      handleAddMembership("community", pendingCommunityId);
                      setPendingCommunityId("");
                    }}
                  >
                    Add
                  </button>
                </div>

              </div>

              {/* Membership Chips */}
              <div className="mt-4">
                {renderMembershipChips(
                  "community",
                  form.communityIds,
                  communityLabelMap,
                  membershipByType.community
                )}
              </div>

              {/* Empty State */}
              {!isLoadingCommunities && communities.length === 0 && (
                <div className="alert alert-warning mt-3 mb-0">
                  No communities are available yet.
                </div>
              )}

              {!isLoadingCommunities &&
                communities.length > 0 &&
                availableCommunities.length === 0 && (
                  <div className="text-muted small mt-3">
                    All communities are already selected.
                  </div>
                )}

            </div>
          )}
        </div>
      </Section>

      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3">
        <button
          className="btn btn-outline-primary ms-sm-auto"
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save affiliation'}
        </button>
      </div>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <form className="card shadow-sm border-0" onSubmit={handleSubmit}>
      <div className="card-body">{content}</div>
    </form>
  );
};

export default AccountAffiliationSection;
